import { useMemo } from 'react'
import Big from 'big.js'
import { energyTableGetTotal, energyTableGetValues } from '../PowerOverviewEnergyTable'
import { useQuery, useSuspenseQuery } from '@apollo/client'
import { GET_WINDOWED_MEASUREMENTS_QUERY } from '../../../../energy/queries/metrics'
import { useRecoilValue } from 'recoil'
import { building, timeframe } from '../../../../store'
import { getTimeframe, TimeframeVariant } from '../../../../utils/getTimeframe'
import { GetGridForPowerOverviewQuery } from '../../../queries/gridForPowerOverview'
import { EnergyTableSingleCategoryColumn } from '../../../../components/Energy/EnergyTable/EnergyTableSingleCategoryColumn'
import { EnergyTypes } from '../../../../components/Energy/EnergyTable/EnergyTableResult'

interface PowerOverviewImportColumnProps {
  maxForBarChart: number
}

export const PowerOverviewImportColumn = ({ maxForBarChart }: PowerOverviewImportColumnProps) => {
  const { activeBuildingId } = useRecoilValue(building)
  const selectedTimeframe = useRecoilValue(timeframe)
  const { start, end, window } = getTimeframe({ ...selectedTimeframe, variant: TimeframeVariant.CONDENSED })

  const { data: gridLayoutData } = useSuspenseQuery(GetGridForPowerOverviewQuery, {
    variables: {
      buildingId: activeBuildingId
    },
    skip: !Boolean(activeBuildingId)
  })

  const { data: energyImported } = useSuspenseQuery(GET_WINDOWED_MEASUREMENTS_QUERY, {
    variables: {
      input: {
        deviceId: gridLayoutData?.getBuilding?.grid?.main?.map(entry => entry.deviceId) ?? [],
        start,
        end,
        measurement: 'energyImported',
        window
      }
    },
    fetchPolicy: 'no-cache',
    skip: !Boolean(gridLayoutData?.getBuilding?.grid?.main && gridLayoutData?.getBuilding?.grid?.main?.length > 0)
  })

  const gridImported = useMemo(() => {
    if (!energyImported?.getWindowedMeasurements || energyImported.getWindowedMeasurements.length < 1)
      return {
        total: 0,
        values: []
      }

    const total = energyTableGetTotal(energyImported.getWindowedMeasurements)
    const values = energyTableGetValues(energyImported.getWindowedMeasurements, selectedTimeframe)

    return {
      total: total.round(0, Big.roundHalfUp).toNumber(),
      values
    }
  }, [energyImported?.getWindowedMeasurements, selectedTimeframe])

  return <EnergyTableSingleCategoryColumn title={'Import'} type={EnergyTypes.Import} total={gridImported.total} resultMax={maxForBarChart} values={gridImported.values} />
}
